import React, { useMemo, useState } from "react";

import { useIntl } from "react-intl";

import { ResourceOverviewData } from "Api/observability/pipeline/types";
import { debounce } from "ds/utils/debounce";
import { useFeatureFlagToggle } from "Reducers/featureFlags/useFeatureFlagToggle";
import { HttpMetricsContext } from "src/organization/pages/project/environment/metrics/http/context";
import { CursorDataType } from "src/organization/pages/project/environment/metrics/http/types";

import { useStubService } from "../Grid/util";
import { ChartTitle } from "../styles";
import { TimeFrameType } from "../types";

import TotalCPU from "./cpu/TotalCPU";
import TotalDisk from "./disk/TotalDisk";
import TotalRAM from "./ram/TotalRAM";
import { TitleHeaderContainer, TotalChartList, TotalContainer } from "./style";
import TotalFilter, { TotalFilterType } from "./TotalFilter";
import { calculateUsage2, getStackKeys2 } from "./utils";
import {
  generateServiceMeta,
  metricsByServiceKind,
  metricsByServiceName
} from "./utils2";

import type { Activity } from "@packages/client";

export type TotalUsageProps = {
  timeframe: TimeFrameType;
  activities: Activity[];
  setTimeFrame: (t: TimeFrameType) => void;
  data?: ResourceOverviewData[];
  setLastUpdated?: (t: Date) => void;
};

const TotalUsage = (props: TotalUsageProps) => {
  const { timeframe, activities, setTimeFrame, data } = props;
  const [filter, setFilter] = useState<TotalFilterType>({
    isKindGroup: false,
    isUsage: true
  });
  const intl = useIntl();
  const [enableInternalMetrics] = useFeatureFlagToggle(
    "ENABLE_INTERNAL_SERVICE_METRICS"
  );
  const [cursorData, setCursorData] = useState<CursorDataType>();
  const debouncedSetCursorData = debounce(setCursorData);

  const deployments = useStubService().all;

  const { stackGroup, stackInfo } = useMemo(() => {
    let serviceMetadata, deployedServiceNames;

    if (!enableInternalMetrics) {
      serviceMetadata = generateServiceMeta({ deployments });
      deployedServiceNames = Array.from(serviceMetadata.keys());
    }

    const { serviceNames, metricsByUnit } = metricsByServiceName(
      data,
      enableInternalMetrics ? undefined : deployedServiceNames
    );

    if (enableInternalMetrics) {
      serviceMetadata = generateServiceMeta({ deployments, serviceNames });
      deployedServiceNames = serviceNames;
    }

    const stackInfo = getStackKeys2(deployedServiceNames, filter);
    const stackGroup = filter.isKindGroup
      ? metricsByServiceKind(metricsByUnit, serviceMetadata)
      : metricsByUnit;
    return { stackGroup, stackInfo };
  }, [data, filter, deployments, enableInternalMetrics]);

  const usage = useMemo(() => calculateUsage2(stackGroup), [stackGroup]);

  return (
    <HttpMetricsContext.Provider
      value={{
        cursorData,
        setCursorData: debouncedSetCursorData,
        timeframe,
        setTimeFrame,
        activities
      }}
    >
      <TotalContainer>
        <TitleHeaderContainer>
          <ChartTitle>
            {intl.formatMessage({
              id: filter.isUsage ? "total_usage" : "total_allocation"
            })}
          </ChartTitle>
          <ChartTitle>
            <TotalFilter filter={filter} onChange={setFilter} />
          </ChartTitle>
        </TitleHeaderContainer>

        <TotalChartList>
          <TotalCPU
            stackInfo={stackInfo}
            data={stackGroup["cpu"]}
            usage={usage?.cpu}
          />
          <hr className="divider" />
          <TotalRAM
            stackInfo={stackInfo}
            data={stackGroup["memory"]}
            usage={usage?.memory}
          />
          <hr className="divider" />
          <TotalDisk
            stackInfo={stackInfo}
            data={stackGroup["/mnt"]}
            usage={usage?.["/mnt"]}
          />
        </TotalChartList>
      </TotalContainer>
    </HttpMetricsContext.Provider>
  );
};

export default TotalUsage;
